import axios from 'axios';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

/**
 * Stok kartı alternatif birimleri. CompanyId gönderilmez.
 * İstek gövdeleri PascalCase; yanıt alanları camelCase okunur.
 */

export interface ItemUnitRow {
  id?: string;
  unitName: string;
  factor: number;
  isDefaultForProduction: boolean;
  sortOrder: number;
  recipeUsageCount?: number;
}

export interface ItemUnitsResponse {
  itemId: string;
  baseUnit: string;
  units: ItemUnitRow[];
}

export interface UnitOption {
  unitName: string;
  factor: number;
  isBase: boolean;
  isDefaultForProduction: boolean;
}

export interface ItemUnitOptions {
  baseUnit: string;
  options: UnitOption[];
}

export interface UnitUsage {
  unitName: string;
  count: number;
}

export class InventoryItemUnitError extends Error {
  code?: string;
  usages: UnitUsage[];

  constructor(message: string, code?: string, usages: UnitUsage[] = []) {
    super(message);
    this.name = 'InventoryItemUnitError';
    this.code = code;
    this.usages = usages;
  }
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
  code?: string;
  warnings?: string[];
}

const fallbackMessage = 'Beklenmeyen bir hata oluştu.';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function mapUnit(raw: unknown): ItemUnitRow | null {
  const row = asRecord(raw);
  if (!row) return null;
  const unitName = text(row.unitName).trim();
  if (!unitName) return null;
  return {
    id: text(row.id) || undefined,
    unitName,
    factor: Number(row.factor ?? 0),
    isDefaultForProduction: row.isDefaultForProduction === true,
    sortOrder: Number(row.sortOrder ?? 0),
    recipeUsageCount: Number(row.recipeUsageCount ?? 0),
  };
}

function mapCard(raw: unknown): ItemUnitsResponse {
  const body = asRecord(raw) ?? {};
  const units = Array.isArray(body.units)
    ? body.units.map(mapUnit).filter((row): row is ItemUnitRow => row != null)
    : [];
  return {
    itemId: text(body.itemId),
    baseUnit: text(body.baseUnit),
    units,
  };
}

function mapOptions(raw: unknown): ItemUnitOptions | null {
  const body = asRecord(raw);
  if (!body) return null;
  const options = Array.isArray(body.options)
    ? body.options.flatMap((item) => {
      const option = asRecord(item);
      if (!option) return [];
      const unitName = text(option.unitName).trim();
      if (!unitName) return [];
      return [{
        unitName,
        factor: Number(option.factor ?? 1),
        isBase: option.isBase === true,
        isDefaultForProduction: option.isDefaultForProduction === true,
      }];
    })
    : [];
  return { baseUnit: text(body.baseUnit), options };
}

function usagesOf(raw: unknown): UnitUsage[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const row = asRecord(item);
    if (!row) return [];
    const unitName = text(row.unitName).trim();
    if (!unitName) return [];
    return [{ unitName, count: Number(row.count ?? 0) }];
  });
}

function toUnitError(error: unknown): InventoryItemUnitError {
  if (error instanceof InventoryItemUnitError) return error;
  if (axios.isAxiosError(error)) {
    const body = asRecord(error.response?.data);
    if (body) {
      return new InventoryItemUnitError(
        text(body.message) || fallbackMessage,
        text(body.code) || undefined,
        usagesOf(body.data),
      );
    }
  }
  if (error instanceof Error && error.message) return new InventoryItemUnitError(error.message);
  return new InventoryItemUnitError(fallbackMessage);
}

function unwrap<T>(body: Envelope<unknown> | undefined, map: (data: unknown) => T): T {
  if (!body || body.success === false || body.data === undefined) {
    throw new InventoryItemUnitError(body?.message || fallbackMessage, body?.code);
  }
  return map(body.data);
}

export async function getItemUnits(itemId: string): Promise<ItemUnitsResponse> {
  try {
    const response = await accountingApi.get<Envelope<unknown>>(ACCOUNTING_API.INVENTORY_ITEM_UNIT(itemId));
    return unwrap(response.data, mapCard);
  } catch (error) {
    throw toUnitError(error);
  }
}

export async function saveItemUnits(itemId: string, units: ItemUnitRow[]): Promise<{ warnings: string[] }> {
  try {
    const response = await accountingApi.post<Envelope<unknown>>(ACCOUNTING_API.INVENTORY_ITEM_UNIT_SAVE(itemId), {
      Units: units.map((unit) => ({
        Id: unit.id ?? null,
        UnitName: unit.unitName,
        Factor: unit.factor,
        IsDefaultForProduction: unit.isDefaultForProduction,
        SortOrder: unit.sortOrder,
      })),
    });
    const body = response.data;
    if (!body || body.success === false) {
      throw new InventoryItemUnitError(body?.message || fallbackMessage, body?.code);
    }
    return { warnings: Array.isArray(body.warnings) ? body.warnings.filter((item) => typeof item === 'string') : [] };
  } catch (error) {
    throw toUnitError(error);
  }
}

export async function getUnitsForItems(itemIds: string[]): Promise<Record<string, ItemUnitOptions>> {
  try {
    const response = await accountingApi.post<Envelope<unknown>>(ACCOUNTING_API.INVENTORY_ITEM_UNIT_BY_ITEMS, {
      ItemIds: itemIds,
    });
    const data = unwrap(response.data, (raw) => raw);
    const record = asRecord(data) ?? {};
    const result: Record<string, ItemUnitOptions> = {};
    for (const [key, value] of Object.entries(record)) {
      const mapped = mapOptions(value);
      if (mapped) result[key.toLowerCase()] = mapped;
    }
    return result;
  } catch (error) {
    throw toUnitError(error);
  }
}
